"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

export default function HeroSection() {
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef?.current) {
        const scrollY = window.scrollY;
        parallaxRef.current.style.transform = `translateY(${scrollY * 0.3}px)`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="relative h-screen min-h-[700px] flex items-center overflow-hidden bg-forest">
      {/* Parallax Background */}
      <div ref={parallaxRef} className="absolute inset-0 w-full h-[120%] -top-[10%]">
        <AppImage
          src="https://images.unsplash.com/photo-1717500252297-b09508db7ceb"
          alt="Estúdio de Pilates com equipamentos modernos e ambiente acolhedor em Porto Alegre"
          fill
          className="object-cover object-center"
          priority />
        
        {/* Multi-layer gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-forest/65 via-forest/30 to-forest/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest/50 via-transparent to-transparent" />
        {/* Bottom vignette */}
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-forest/90 to-transparent" />
      </div>
      {/* Floating stat card */}
      <div className="absolute top-1/4 right-8 md:right-16 z-20 hidden md:block float-card">
        <div className="glass-card rounded-3xl p-5 w-44">
          <p className="text-xs text-stone uppercase tracking-widest mb-1">Alunos Ativos</p>
          <p className="font-serif text-3xl text-forest font-medium">1.200+</p>
          <div className="divider-gold mt-3" />
          <p className="text-xs text-stone mt-2">3 unidades em POA</p>
        </div>
      </div>
      <div className="absolute top-2/3 right-16 md:right-32 z-20 hidden lg:block float-card-slow" style={{ animationDelay: "1.5s" }}>
        <div className="glass-card rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center flex-shrink-0">
            <svg width="12" height="12" fill="white" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-semibold text-charcoal">Avaliação Google</p>
            <p className="text-xs text-stone">4.9 ★ · 340 avaliações</p>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="relative z-10 max-w-8xl mx-auto px-6 md:px-10 w-full">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="hero-badge-animate inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm mb-8 md:mb-10">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            <span className="text-cream text-xs tracking-widest uppercase font-medium">
              Porto Alegre · 3 Unidades
            </span>
          </div>

          <h1 className="hero-animate-1 font-serif text-cream leading-[1.0] tracking-tight mb-6"
          style={{ fontSize: 'clamp(3rem, 8vw, 6rem)' }}>
            Pilates para{" "}
            <span className="italic font-light" style={{ color: 'var(--gold-light)' }}>Saúde,</span>
            <br />
            Movimento e{" "}
            <span className="italic font-light opacity-85">Bem-Estar</span>
          </h1>

          <p className="hero-animate-2 text-cream/75 leading-relaxed mb-10 max-w-xl"
          style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)' }}>
            Studio especializado em Pilates e terapias integrativas em Porto Alegre.
            Cuide do seu corpo com profissionais dedicados ao seu bem-estar.
          </p>

          <div className="hero-animate-3 flex flex-col sm:flex-row gap-4">
            <a
              href="https://wa.me/5551999999999?text=Olá! Gostaria de agendar uma aula experimental."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-hero-gold">
              
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Agendar Aula Experimental
            </a>
            <button
              onClick={() => document.getElementById("unidades")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-ghost-white">
              
              Selecionar Unidade
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Trust indicators */}
          <div className="hero-animate-4 flex items-center gap-6 mt-10 pt-8 border-t border-white/10">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {[1, 2, 3]?.map((i) =>
                <div key={i} className="w-7 h-7 rounded-full bg-sage-pale border-2 border-white/30 flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="text-forest">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                    </svg>
                  </div>
                )}
              </div>
              <span className="text-cream/60 text-xs">+1.200 alunos</span>
            </div>
            <div className="w-px h-4 bg-white/20" />
            <div className="flex items-center gap-1.5">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-gold">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span className="text-cream/60 text-xs">4.9 no Google</span>
            </div>
            <div className="w-px h-4 bg-white/20" />
            <span className="text-cream/60 text-xs">15+ anos de experiência</span>
          </div>
        </div>
      </div>
      {/* Bottom cream fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-cream to-transparent z-10" />
    </section>);

}